import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/hooks/queryKeys";
import { useUiStore } from "@/stores/ui.store";
import { MAX_LENGTHS } from "@/lib/validation";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Card, CardContent } from "@/components/ui/Card";
import { Typography } from "@/components/ui/Typography";

const UNIT_TYPES = [
  { value: "STUDIO", label: "Studio" },
  { value: "ONE_BHK", label: "1 BHK" },
  { value: "TWO_BHK", label: "2 BHK" },
  { value: "THREE_BHK", label: "3 BHK" },
  { value: "FOUR_BHK", label: "4 BHK" },
  { value: "OTHER", label: "Other" },
] as const;

export function AddUnitPage() {
  const params = useParams();
  const navigate = useNavigate();
  const propertyId = params.id as string;
  const queryClient = useQueryClient();
  const addToast = useUiStore((s) => s.addToast);

  const [propertyName, setPropertyName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    alias: "",
    unitNumber: "",
    unitType: "ONE_BHK" as string,
    maxTenants: "1",
    rentAmount: "",
    dueDate: "1",
    securityDeposit: "",
    notes: "",
  });

  useEffect(() => {
    async function load() {
      try {
        const prop = await api.get<{ name: string }>(`/properties/${propertyId}`);
        setPropertyName(prop.name);
      } catch {
        addToast({ type: "error", message: "Failed to load property" });
        navigate("/properties");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [propertyId, navigate, addToast]);

  function update(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const rentAmount = parseFloat(form.rentAmount);
    const dueDate = parseInt(form.dueDate, 10);
    const maxTenants = parseInt(form.maxTenants, 10);
    const newErrors: Record<string, string> = {};
    if (!form.alias.trim()) newErrors.alias = "Unit alias is required";
    else if (form.alias.length > MAX_LENGTHS.unitAlias) newErrors.alias = `Unit alias must be at most ${MAX_LENGTHS.unitAlias} characters`;
    if (!form.unitNumber.trim()) newErrors.unitNumber = "Unit number is required";
    else if (form.unitNumber.length > MAX_LENGTHS.unitNumber) newErrors.unitNumber = `Unit number must be at most ${MAX_LENGTHS.unitNumber} characters`;
    if (!rentAmount || rentAmount <= 0) newErrors.rentAmount = "Valid rent amount is required";
    if (dueDate < 1 || dueDate > 31) newErrors.dueDate = "Due date must be 1–31";
    if (!maxTenants || maxTenants < 1 || maxTenants > 20) newErrors.maxTenants = "Max tenants must be 1–20";
    if (form.notes.length > MAX_LENGTHS.notes) newErrors.notes = `Notes must be at most ${MAX_LENGTHS.notes} characters`;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/units", {
        propertyId,
        alias: form.alias.trim(),
        unitNumber: form.unitNumber.trim(),
        unitType: form.unitType,
        maxTenants,
        rentAmount,
        dueDate: dueDate || 1,
        securityDeposit: form.securityDeposit.trim() ? parseFloat(form.securityDeposit) : undefined,
        noticePeriodDays: 30,
        notes: form.notes.trim() || undefined,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDetail(propertyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.properties() });
      addToast({ type: "success", message: "Unit added" });
      navigate(`/property/${propertyId}`);
    } catch (err) {
      addToast({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to add unit",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <Link
          to={`/property/${propertyId}`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to {propertyName}
        </Link>
        <Typography variant="h1">Add Unit</Typography>
        <p className="mt-1 text-sm text-slate-500">Add a new unit to {propertyName}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Unit alias"
                value={form.alias}
                onChange={(e) => update("alias", e.target.value)}
                error={errors.alias}
                placeholder="e.g. Ground floor, Block A"
                required
              />
              <Input
                label="Unit number"
                value={form.unitNumber}
                onChange={(e) => update("unitNumber", e.target.value)}
                error={errors.unitNumber}
                placeholder="e.g. 101, A-1"
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Select
                label="Property type"
                options={UNIT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                value={form.unitType}
                onChange={(e) => update("unitType", e.target.value)}
              />
              <Input
                label="Max tenants"
                type="number"
                min="1"
                max="20"
                value={form.maxTenants}
                onChange={(e) => update("maxTenants", e.target.value)}
                error={errors.maxTenants}
                placeholder="1"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Monthly rent (₹)"
                type="number"
                min="1"
                value={form.rentAmount}
                onChange={(e) => update("rentAmount", e.target.value)}
                error={errors.rentAmount}
                required
              />
              <Input
                label="Rent due date (1–31)"
                type="number"
                min="1"
                max="31"
                value={form.dueDate}
                onChange={(e) => update("dueDate", e.target.value)}
                error={errors.dueDate}
              />
            </div>
            <Input
              label="Security deposit (₹)"
              type="number"
              min="0"
              value={form.securityDeposit}
              onChange={(e) => update("securityDeposit", e.target.value)}
              placeholder="Optional"
            />
            <Input
              label="Notes"
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Optional"
              maxLength={MAX_LENGTHS.notes}
              error={errors.notes}
            />
          </CardContent>
        </Card>

        <div className="flex gap-3 mt-6">
          <Link to={`/property/${propertyId}`}>
            <Button variant="secondary" type="button">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={submitting}>
            Add Unit
          </Button>
        </div>
      </form>
    </div>
  );
}
